import { datadogRum } from '@datadog/browser-rum';
import { IconName } from '@foam/buoy/src/components/Icon/types';
import { Modal, ModalContent } from '@foam/buoy/src/components/Modal/Modal';
import { Form, Formik, FormikHelpers, FormikProps } from 'formik';
import { useCallback, useEffect, useState } from 'react';
import { match } from 'ts-pattern';
import { useSharedLinkAccessEmail } from '~/hooks/api/useSavedLists/useSharedLinkAccessEmail';
import { ICreator } from '~/types';

import { ContactInfoStep } from './ContactInfoStep';
import { MessageStep } from './MessageStep';
import { useMessageToolStore } from './useMessageToolStore';
import { trackMessageToolEvent, validate } from './utils';

export interface IMessageToolModalProps {
  onSubmit: (
    data: MessageToolFormValues & { creator: ICreator },
  ) => void | Promise<void>;
  onClose?: () => void;
}

export interface MessageToolFormValues {
  name: string;
  email: string;
  company: string;
  message: string;
}

export interface MessageTemplate {
  id: string;
  event_name: string;
  title: string;
  icon: IconName;
  message?: string;
}

export const MessageToolModal: React.FC<IMessageToolModalProps> = ({
  onSubmit,
  onClose,
}) => {
  const {
    isModalOpen: isMessageToolModalOpen,
    step,
    creator,
    isLoading,
    list,
    link,
    setIsLoading,
    closeModal: closeMessageToolModal,
    setStep,
  } = useMessageToolStore();
  const { accessEmail } = useSharedLinkAccessEmail();

  const [isConfirmationModalOpen, setIsConfirmationModalOpen] = useState(false);

  const handleSubmit = useCallback(
    async (
      values: MessageToolFormValues,
      { resetForm }: FormikHelpers<MessageToolFormValues>,
    ) => {
      if (!creator) return;
      setIsLoading(true);
      try {
        await onSubmit({ ...values, creator });
        resetForm();
        closeMessageToolModal();

        if (!list || !link) return;
        trackMessageToolEvent({
          eventType: 'message_submitted',
          creator,
          list,
          link,
          values,
        });
      } catch (error) {
        datadogRum.addError(error, {
          errorInfo: 'foam_shared_list_message_send_failed',
        });
      } finally {
        setIsLoading(false);
      }
    },
    [creator, onSubmit, closeMessageToolModal, setIsLoading],
  );

  const handleCloseModal = useCallback(
    ({ resetForm, dirty }: Partial<FormikProps<MessageToolFormValues>>) => {
      if (dirty) {
        setIsConfirmationModalOpen(true);
      } else {
        setIsConfirmationModalOpen(false);
        closeMessageToolModal();
        resetForm?.();
        onClose?.();
      }
    },
    [closeMessageToolModal, onClose, setIsConfirmationModalOpen],
  );

  useEffect(() => {
    if (!isMessageToolModalOpen || !creator || !list || !link) return;
    trackMessageToolEvent({ eventType: 'modal_opened', creator, list, link });
  }, [isMessageToolModalOpen, creator, list, link]);

  return (
    <Formik
      enableReinitialize={false}
      initialValues={{
        message: '',
        name: '',
        email: accessEmail ?? '',
        company: '',
      }}
      onSubmit={handleSubmit}
      validate={validate}
    >
      {(formikProps) => (
        <>
          <Modal
            title={
              step === 1
                ? `Ask ${creator?.manager?.name} a question`
                : 'Your details'
            }
            isOpen={isMessageToolModalOpen}
            onClose={() => handleCloseModal(formikProps)}
            primaryAction={{
              label: step === 1 ? 'Next' : 'Send',
              onClick: async () => {
                if (step === 1) {
                  setStep(2);
                } else {
                  await formikProps.submitForm();
                }
              },
              isDisabled:
                (step === 1 && !formikProps.values.message) ||
                formikProps.values.message.trim() === '' ||
                (step === 2 && (isLoading || formikProps.isSubmitting)),
            }}
            secondaryAction={{
              label: step === 1 ? 'Cancel' : 'Back',
              onClick: () => {
                if (step === 1) {
                  handleCloseModal(formikProps);
                } else {
                  setStep(1);
                }
              },
            }}
          >
            <Form>
              {match(step)
                .with(1, () => <MessageStep />)
                .with(2, () => <ContactInfoStep />)
                .otherwise(() => (
                  <></>
                ))}
            </Form>
          </Modal>
          <Modal
            isOpen={isConfirmationModalOpen}
            onClose={() => setIsConfirmationModalOpen(false)}
            title="Are you sure you want to discard your message?"
            primaryAction={{
              label: 'Confirm',
              onClick: () =>
                handleCloseModal({
                  resetForm: formikProps.resetForm,
                  dirty: false,
                }),
            }}
            secondaryAction={{
              label: 'Cancel',
              onClick: () => setIsConfirmationModalOpen(false),
            }}
          >
            <ModalContent text="Your changes may not be saved." />
          </Modal>
        </>
      )}
    </Formik>
  );
};
